import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from 'react-beautiful-dnd';

import { doDeleteCommunity } from 'store/actionCreators/communities';
import { doUpdateCommunitiesOrder } from 'store/actionCreators/communities';
import { getProfileName } from 'utils/common';
import { ROUTES, PROFILE_ROUTES } from 'configs';
import {
  Loader,
  DefaultCommunityImage,
  ProfileName,
  PopupButtonMessage,
  DragIcon,
} from 'components';
import { VIEW_MEMBER } from 'constants/sidebars';
import { openModal } from 'store/actionCreators/modal';
import {
  getCommunitiesLoading,
  getDeleteCommunityLoading,
} from 'store/selectors/communities';
import { doDeleteCommunityUser } from 'store/actionCreators/currentCommunity';
import { isDeleteCommunityUserLoading } from 'store/selectors/currentCommunity';
import { CommunityListItem } from 'types';
import { getCommunities } from 'store/selectors/communities';

import {
  Community,
  StyledFloatingLoader,
  ListWrapper,
  DragHandleCell,
  DragHandle,
  Header,
  HeaderColumn,
  Row,
  RowColumn,
  ListLoader,
  EmptyState,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateDescription,
} from './styled';

interface CommunitiesListProps {
  data: CommunityListItem[];
  userId: string;
  withDelete?: boolean;
  withLeave?: boolean;
  emptyStateType?: 'owner' | 'member' | 'general';
}
const CommunitiesList: FC<CommunitiesListProps> = ({
  data,
  withDelete,
  withLeave,
  userId,
  emptyStateType = 'general',
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { addToast } = useToasts();
  const isFetching = useSelector(getCommunitiesLoading);
  const isDeletingUser = useSelector(isDeleteCommunityUserLoading);
  const isDeletingCommunity = useSelector(getDeleteCommunityLoading);
  const allCommunities = useSelector(getCommunities);

  const successMessage = (message) =>
    addToast(message, {
      appearance: 'success',
      autoDismiss: true,
    });

  const redirectToCommunity = (id: string) => {
    navigate(`${ROUTES.COMMUNITY}/${id}`);
  };

  const handleDeleteCommunity = (id) => {
    dispatch(
      doDeleteCommunity({
        data: id,
        onSuccess: () => successMessage(t('community.deleteSuccess')),
      }),
    );

    navigate(`${ROUTES.PROFILE}/${PROFILE_ROUTES.COMMUNITIES}`);
  };

  const handleLeaveFromCommunity = (id) => {
    dispatch(
      doDeleteCommunityUser({
        communityId: id,
        onSuccess: () => successMessage(t('community.leaveSuccess')),
        userIds: [userId],
        isCurrentUser: true,
      }),
    );
  };

  // Drag-and-drop handler
  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const sourceId = data[result.source.index].id;
    const destId = data[result.destination.index].id;
    const sourceIndexInAll = allCommunities.findIndex((c) => c.id === sourceId);
    const destIndexInAll = allCommunities.findIndex((c) => c.id === destId);
    if (sourceIndexInAll === -1 || destIndexInAll === -1) return;
    const newOrder = Array.from(allCommunities);
    const [removed] = newOrder.splice(sourceIndexInAll, 1);
    newOrder.splice(destIndexInAll, 0, removed);
    // Save to localStorage
    localStorage.setItem(
      'communityOrder',
      JSON.stringify(newOrder.map((c: any) => c.id)),
    );
    const movedCommunity = allCommunities[sourceIndexInAll];
    const newPosition = destIndexInAll;
    dispatch(
      doUpdateCommunitiesOrder.trigger({
        communities: newOrder,
        communityId: movedCommunity.id,
        end: newPosition,
      }),
    );
  };

  if (!data || data.length === 0) {
    return (
      <ListWrapper>
        <Header>
          <HeaderColumn>
            {t('profileCommunities.table.labelCommunity')}
          </HeaderColumn>
          <HeaderColumn>{t('communitySettings.owner')}</HeaderColumn>
          <HeaderColumn center>{t('common.groups')}</HeaderColumn>
          <HeaderColumn center>{t('community.members')}</HeaderColumn>
          <HeaderColumn />
        </Header>
        <EmptyState>
          <EmptyStateIcon>
            {emptyStateType === 'owner'
              ? '👑'
              : emptyStateType === 'member'
              ? '👥'
              : '🏠'}
          </EmptyStateIcon>
          <EmptyStateTitle>
            {/* {emptyStateType === 'owner'
              ? */}
            {t('profileCommunities.empty.title', 'No Communities Owned')}
            {/* // : emptyStateType === 'member'
              // ? t('profileCommunities.empty.title', 'No Community Memberships')
              // : t(
              //     'profileCommunities.emptyGeneral.title',
              //     'No Communities Found',
              //   )} */}
          </EmptyStateTitle>
          <EmptyStateDescription>
            {/* {emptyStateType === 'owner'
              ? t(
                  'profileCommunities.empty.description',
                  "You haven't created any communities yet. Start building your community to organize events and manage members.",
                ) */}
            <Trans
              i18nKey="profileCommunities.empty.description"
              components={{ brPure: <br /> }}
            />
            {/* : emptyStateType === 'member'
              ? t(
                  'profileCommunities.empty.description',
                  "You're not a member of any communities yet. Join existing communities or create your own to get started.",
                )
              : t(
                  'profileCommunities.emptyGeneral.description',
                  'No communities available. Create your own community or join existing ones to start collaborating.',
                )} */}
          </EmptyStateDescription>
        </EmptyState>
      </ListWrapper>
    );
  }

  return (
    <ListWrapper>
      {(isDeletingUser || isDeletingCommunity) && (
        <StyledFloatingLoader>
          <Loader size="60px" type={null} />
        </StyledFloatingLoader>
      )}
      <DragDropContext onDragEnd={onDragEnd}>
        <div>
          <Header>
            <HeaderColumn>
              {t('profileCommunities.table.labelCommunity')}
            </HeaderColumn>
            <HeaderColumn>{t('communitySettings.owner')}</HeaderColumn>
            <HeaderColumn center>{t('common.groups')}</HeaderColumn>
            <HeaderColumn center>{t('community.members')}</HeaderColumn>
            <HeaderColumn />
          </Header>
          <Droppable droppableId="communities-list">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {data.map((item, index) => (
                  <Draggable key={item.id} draggableId={item.id} index={index}>
                    {(provided, snapshot) => (
                      <div ref={provided.innerRef} {...provided.draggableProps}>
                        <Row data-rbd-dragging={snapshot.isDragging}>
                          <RowColumn>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                height: '70px',
                              }}
                            >
                              <DragHandleCell>
                                <DragHandle {...provided.dragHandleProps}>
                                  <DragIcon />
                                </DragHandle>
                              </DragHandleCell>
                              <Community>
                                <Community.Wrapper
                                  onClick={() => redirectToCommunity(item.id)}
                                >
                                  <Community.Logo>
                                    {!item.logo ? (
                                      <DefaultCommunityImage
                                        title={item.name
                                          .charAt(0)
                                          .toUpperCase()}
                                      />
                                    ) : (
                                      <Community.Logo.Image src={item.logo} />
                                    )}
                                  </Community.Logo>
                                  <Community.Name>{item.name}</Community.Name>
                                </Community.Wrapper>
                              </Community>
                            </div>
                          </RowColumn>
                          <RowColumn>
                            <ProfileName
                              name={getProfileName(item.communityOwner)}
                              avatar={
                                item.communityOwner.smallLogo ||
                                item.communityOwner.logo
                              }
                              onClick={() => {
                                dispatch(
                                  openModal({
                                    type: VIEW_MEMBER,
                                    placement: 'right',
                                    data: { profile: item.communityOwner },
                                  }),
                                );
                              }}
                            />
                          </RowColumn>
                          <RowColumn center>{item.groupsCount || 0}</RowColumn>
                          <RowColumn center>{item.usersCount || 0}</RowColumn>
                          {withDelete && (
                            <RowColumn>
                              <PopupButtonMessage
                                popupButtonText={t('common.delete')}
                                popupHeaderText={t(
                                  'communitySettings.deleteCommunityHeader',
                                )}
                                popupMessageText={t(
                                  'communitySettings.deleteCommunityMessage',
                                )}
                                onConfirm={() => handleDeleteCommunity(item.id)}
                              />
                            </RowColumn>
                          )}
                          {withLeave && (
                            <RowColumn>
                              <PopupButtonMessage
                                popupButtonText={t(
                                  'profileCommunities.leaveCommunity.okButton',
                                )}
                                cancelButtonText={t(
                                  'profileCommunities.leaveCommunity.cancelButton',
                                )}
                                popupHeaderText={t(
                                  'profileCommunities.leaveCommunity.title',
                                )}
                                popupMessageText={t(
                                  'profileCommunities.leaveCommunity.message',
                                )}
                                size="lg"
                                ButtonComponent={Community.Icon}
                                onConfirm={() =>
                                  handleLeaveFromCommunity(item.id)
                                }
                              />
                            </RowColumn>
                          )}
                        </Row>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      </DragDropContext>
      {isFetching && (
        <ListLoader>
          <Loader type="button" size="28px" thickness={2} />
        </ListLoader>
      )}
    </ListWrapper>
  );
};

export default CommunitiesList;
